import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faDownload, faTrash } from '@fortawesome/free-solid-svg-icons';

export default function PrivacySettings() {
  const { user } = useAuth();
  const { familyId } = useFamily();
  const [loading, setLoading] = useState(false);
  const [allowSharing, setAllowSharing] = useState(true);

  useEffect(() => {
    if (user) {
      const loadPreferences = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setAllowSharing(data.preferences?.privacy?.allowSharing ?? true);
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
        }
      };
      loadPreferences();
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.exists() ? userDoc.data() : {};

      await updateDoc(userDocRef, {
        preferences: {
          ...currentData.preferences,
          privacy: {
            allowSharing,
          },
        },
        updatedAt: new Date(),
      });

      toast.success('Privacy settings updated!');
    } catch (error) {
      console.error('Error updating privacy:', error);
      toast.error('Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!user || !familyId) return;

    try {
      // Collect all user data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      
      const experiencesQuery = query(
        collection(db, 'projects'),
        where('familyId', '==', familyId)
      );
      const experiencesSnapshot = await getDocs(experiencesQuery);
      
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('familyId', '==', familyId)
      );
      const conversationsSnapshot = await getDocs(conversationsQuery);

      const exportData = {
        user: userDoc.exists() ? userDoc.data() : null,
        family: familyDoc.exists() ? familyDoc.data() : null,
        experiences: experiencesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        conversations: conversationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        exportedAt: new Date().toISOString(),
      };

      // Download as JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cultural-judaism-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const handleDeleteAccount = async () => {
    const confirm1 = window.prompt(
      'This will permanently delete your account and all data. Type "DELETE" to confirm:'
    );
    
    if (confirm1 !== 'DELETE') {
      return;
    }

    const confirm2 = window.confirm(
      'Are you absolutely sure? This action cannot be undone. All your experiences, conversations, and family data will be permanently deleted.'
    );

    if (!confirm2) {
      return;
    }

    toast.error('Account deletion is not yet implemented. Please contact support if you need to delete your account.');
    // TODO: Implement account deletion
    // This would require:
    // 1. Delete all user's projects
    // 2. Delete all user's conversations
    // 3. Remove user from family
    // 4. Delete user document
    // 5. Delete Firebase Auth account
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-6">Privacy & Data</h2>

      {/* Community Sharing */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-text-primary">Allow Anonymous Sharing</label>
            <p className="text-xs text-text-secondary">Allow your tips to be shared anonymously in the community</p>
          </div>
          <button
            onClick={() => setAllowSharing(!allowSharing)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              allowSharing ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                allowSharing ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="mt-4 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faSave} />
          <span>{loading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="border-t border-border pt-6 space-y-4">
        {/* Data Export */}
        <div>
          <h3 className="text-base font-semibold text-text-primary mb-2">Export Your Data</h3>
          <p className="text-sm text-text-secondary mb-4">
            Download all your data including experiences, conversations, and family information as a JSON file.
          </p>
          <button
            onClick={handleExportData}
            className="px-6 py-3 bg-surface text-text-primary rounded-lg font-semibold hover:bg-[#E0E0E0] transition-all flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>Export Data</span>
          </button>
        </div>

        {/* Delete Account */}
        <div>
          <h3 className="text-base font-semibold text-red-600 mb-2">Delete Account</h3>
          <p className="text-sm text-text-secondary mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            onClick={handleDeleteAccount}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>Delete Account</span>
          </button>
        </div>
      </div>
    </div>
  );
}

